import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import {
  Buildings, Envelope, Phone, CalendarCheck, ChartLineUp,
  ArrowsLeftRight, CurrencyInr, Crown, Sparkle, IdentificationCard,
  PencilSimple, FloppyDisk, X, Globe, WhatsappLogo, User, Timer,
  ShieldCheck, CalendarBlank
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";

function getMemberAge(createdAt) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const days = Math.floor((now - created) / (1000 * 60 * 60 * 24));
  if (days < 30) return "New Member";
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "1 month old member" : `${months} months old member`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (remMonths === 0) return years === 1 ? "1 year old member" : `${years} years old member`;
  return `${years} year${years > 1 ? "s" : ""} ${remMonths} month${remMonths > 1 ? "s" : ""} old member`;
}

export default function ProfilePage() {
  const { userId } = useParams();
  const { token, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const isOwnProfile = !userId || userId === user?.id;
  const targetUserId = userId || user?.id;

  useEffect(() => { fetchProfile(); }, [targetUserId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/members/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setForm({
        full_name: response.data.full_name || "",
        phone: response.data.phone || "",
        business_name: response.data.business_name || "",
        about_user: response.data.about_user || "",
        about_company: response.data.about_company || "",
        website_link: response.data.website_link || "",
        whatsapp_number: response.data.whatsapp_number || "",
      });
    } catch (error) {
      toast.error("Failed to load profile");
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/update`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully!");
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally { setSaving(false); }
  };

  const getPerformanceInsight = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/insights`, { insight_type: "performance" },
        { headers: { Authorization: `Bearer ${token}` } });
      setAiInsight(response.data.insight);
    } catch { toast.error("Failed to get AI insights"); }
    finally { setAiLoading(false); }
  };

  const getPitchInsight = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/insights`, { insight_type: "pitch" },
        { headers: { Authorization: `Bearer ${token}` } });
      setAiInsight(response.data.insight);
    } catch { toast.error("Failed to generate pitch"); }
    finally { setAiLoading(false); }
  };

  if (loading) return (
    <>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
      </div>
    </>
  );

  if (!profile) return (
    <>
      <div className="text-center py-20"><p className="text-secondary">Profile not found</p></div>
    </>
  );

  const roleBadge = {
    president: { bg: "bg-bch-gold/20", text: "text-bch-gold", label: "President" },
    vice_president: { bg: "bg-bch-teal/20", text: "text-bch-teal", label: "Vice President" },
    secretary: { bg: "bg-purple-500/20", text: "text-purple-400", label: "Secretary" },
    super_admin: { bg: "bg-red-500/20", text: "text-red-400", label: "Super Admin" }
  }[profile.role] || null;

  const memberAge = getMemberAge(profile.created_at);

  return (
    <>
      <SEO title="My Profile" description="View and manage your BCH member profile." path="/profile" noIndex />
      <div className="space-y-6 animate-fadeInUp">
        {/* Header Card */}
        <div className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-bch-teal/20 to-bch-gold/20" />
          <div className="px-8 pb-6">
            <div className="flex items-end gap-6 -mt-12">
              <div className="w-24 h-24 rounded-full bg-bch-teal flex items-center justify-center border-4 border-[var(--bg-card)]">
                {profile.profile_picture ? (
                  <img src={profile.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-3xl">{profile.full_name.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-primary">{profile.full_name}</h1>
                  {roleBadge && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleBadge.bg} ${roleBadge.text}`}>
                      <Crown className="w-3 h-3 inline mr-1" weight="fill" />{roleBadge.label}
                    </span>
                  )}
                  {memberAge && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-sky-500/15 text-sky-500" data-testid="member-age-badge">
                      <Timer className="w-3 h-3 inline mr-1" weight="fill" />{memberAge}
                    </span>
                  )}
                </div>
                <p className="text-bch-gold text-lg">{profile.business_name}</p>
              </div>
              <div className="flex gap-2">
                {isOwnProfile && !editing && (
                  <Button onClick={() => setEditing(true)} variant="outline"
                    className="border-bch-teal/30 text-bch-teal hover:bg-bch-teal/10" data-testid="edit-profile-btn">
                    <PencilSimple className="w-4 h-4 mr-2" weight="duotone" /> Edit Profile
                  </Button>
                )}
                {isOwnProfile && (
                  <Link to="/id-card">
                    <Button variant="outline" className="border-bch-gold/30 text-bch-gold hover:bg-bch-gold/10">
                      <IdentificationCard className="w-5 h-5 mr-2" weight="duotone" /> ID Card
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        {editing && isOwnProfile && (
          <div className="bg-card border border-bch-teal/30 rounded-2xl p-6 space-y-5" data-testid="edit-profile-form">
            <div className="flex items-center justify-between">
              <h2 className="text-primary font-semibold text-lg">Edit Profile</h2>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-muted">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted mb-1.5 block">Full Name</label>
                <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}
                  className="bg-surface border-[var(--border-color)]" data-testid="edit-full-name" />
              </div>
              <div>
                <label className="text-sm text-muted mb-1.5 block">Phone</label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="bg-surface border-[var(--border-color)]" data-testid="edit-phone" />
              </div>
              <div>
                <label className="text-sm text-muted mb-1.5 block">Business Name</label>
                <Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})}
                  className="bg-surface border-[var(--border-color)]" data-testid="edit-business-name" />
              </div>
              <div>
                <label className="text-sm text-muted mb-1.5 block">Website</label>
                <Input value={form.website_link} onChange={e => setForm({...form, website_link: e.target.value})}
                  placeholder="https://..." className="bg-surface border-[var(--border-color)]" data-testid="edit-website" />
              </div>
              <div>
                <label className="text-sm text-muted mb-1.5 block">WhatsApp Number</label>
                <Input value={form.whatsapp_number} onChange={e => setForm({...form, whatsapp_number: e.target.value})}
                  placeholder="+91..." className="bg-surface border-[var(--border-color)]" data-testid="edit-whatsapp" />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted mb-1.5 block">About You</label>
              <textarea value={form.about_user} onChange={e => setForm({...form, about_user: e.target.value})}
                rows={3} className="w-full bg-surface border border-[var(--border-color)] rounded-lg p-3 text-sm text-primary resize-none focus:outline-none focus:border-bch-teal/50"
                data-testid="edit-about-user" />
            </div>
            <div>
              <label className="text-sm text-muted mb-1.5 block">About Company</label>
              <textarea value={form.about_company} onChange={e => setForm({...form, about_company: e.target.value})}
                rows={3} className="w-full bg-surface border border-[var(--border-color)] rounded-lg p-3 text-sm text-primary resize-none focus:outline-none focus:border-bch-teal/50"
                data-testid="edit-about-company" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setEditing(false)} className="text-muted" data-testid="cancel-edit-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving}
                className="bg-bch-teal hover:bg-bch-teal/80 text-white" data-testid="save-profile-btn">
                <FloppyDisk className="w-4 h-4 mr-2" weight="duotone" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6">
              <h2 className="text-primary font-semibold mb-4">Contact Information</h2>
              <div className="space-y-4">
                <InfoRow icon={Envelope} value={profile.email} />
                <InfoRow icon={Phone} value={profile.phone} />
                <InfoRow icon={Buildings} value={profile.chapter_name || "Not assigned"} />
                {profile.website_link && <InfoRow icon={Globe} value={profile.website_link} isLink />}
                {profile.whatsapp_number && <InfoRow icon={WhatsappLogo} value={profile.whatsapp_number} />}
              </div>
            </div>

            {/* Business Category */}
            <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6">
              <h2 className="text-primary font-semibold mb-4">Business Category</h2>
              <span className="px-4 py-2 bg-bch-teal/20 text-bch-teal rounded-2xl inline-block">
                {profile.business_category}
              </span>
            </div>

            {/* Membership - own profile only */}
            {isOwnProfile && (
              <div className="bg-card border border-bch-gold/20 rounded-2xl p-6" data-testid="membership-card">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-5 h-5 text-bch-gold" weight="fill" />
                  <h2 className="text-bch-gold font-semibold">Membership Details</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted text-sm flex items-center gap-2"><CalendarBlank className="w-4 h-4" />Joined</span>
                    <span className="text-primary text-sm font-medium">
                      {profile.created_at ? new Date(profile.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted text-sm flex items-center gap-2"><Timer className="w-4 h-4" />Member Since</span>
                    <span className="text-sky-500 text-sm font-semibold">{memberAge || "N/A"}</span>
                  </div>
                  {profile.membership_expiry && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Valid Until</span>
                      <span className="text-bch-gold text-sm font-mono font-semibold">
                        {new Date(profile.membership_expiry).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                    </div>
                  )}
                  {profile.membership_expiry && (() => {
                    const expiry = new Date(profile.membership_expiry);
                    const now = new Date();
                    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                    const isExpired = daysLeft <= 0;
                    return (
                      <div className={`mt-2 px-4 py-2.5 rounded-xl text-center text-sm font-medium ${
                        isExpired ? "bg-red-500/15 text-red-400" : daysLeft <= 30 ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"
                      }`} data-testid="membership-status">
                        {isExpired ? "Membership Expired" : `${daysLeft} days remaining`}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Stats */}
          <div className="space-y-6">
            <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6">
              <h2 className="text-primary font-semibold mb-6">Performance Stats</h2>
              <div className="space-y-6">
                <StatRow icon={CurrencyInr} label="Business Gained" value={`\u20B9${(profile.business_gained || 0).toLocaleString("en-IN")}`} color="gold" />
                <StatRow icon={ArrowsLeftRight} label="Referrals Given" value={profile.referrals_count || 0} color="teal" />
                <StatRow icon={CalendarCheck} label="Attendance" value={`${profile.attendance_percentage || 0}%`} color="teal" />
                <StatRow icon={ChartLineUp} label="Total Savings" value={`\u20B9${(profile.total_savings || 0).toLocaleString("en-IN")}`} color="gold" />
              </div>
            </div>

            {/* About sections */}
            {profile.about_user && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6">
                <h2 className="text-primary font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4 text-bch-teal" />About</h2>
                <p className="text-secondary text-sm leading-relaxed">{profile.about_user}</p>
              </div>
            )}
            {profile.about_company && (
              <div className="bg-card border border-[var(--border-color)] rounded-2xl p-6">
                <h2 className="text-primary font-semibold mb-3 flex items-center gap-2"><Buildings className="w-4 h-4 text-bch-gold" />About Company</h2>
                <p className="text-secondary text-sm leading-relaxed">{profile.about_company}</p>
              </div>
            )}
          </div>

          {/* Right Column - AI Features (Own profile only) */}
          {isOwnProfile && (
            <div className="space-y-6">
              <div className="bg-card border border-bch-gold/30 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkle className="w-5 h-5 text-bch-gold" weight="fill" />
                  <h2 className="text-bch-gold font-semibold">AI Assistant</h2>
                </div>
                <p className="text-secondary text-sm mb-4">Get personalized insights and recommendations powered by AI.</p>
                <div className="space-y-3">
                  <Button onClick={getPerformanceInsight} disabled={aiLoading}
                    className="w-full bg-bch-teal/20 text-bch-teal hover:bg-bch-teal/30">
                    {aiLoading ? "Analyzing..." : "Performance Analysis"}
                  </Button>
                  <Button onClick={getPitchInsight} disabled={aiLoading}
                    className="w-full bg-bch-gold/20 text-bch-gold hover:bg-bch-gold/30">
                    {aiLoading ? "Generating..." : "Generate Elevator Pitch"}
                  </Button>
                </div>
              </div>

              {aiInsight && (
                <div className="bg-card border border-bch-gold/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-bch-gold font-semibold">AI Insight</h3>
                    <button onClick={() => setAiInsight(null)} className="text-muted text-sm hover:text-secondary">Dismiss</button>
                  </div>
                  <div className="text-secondary text-sm prose prose-sm max-w-none prose-headings:text-primary prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1 prose-strong:text-primary prose-ul:my-1 prose-li:my-0.5 prose-p:my-1 prose-hr:my-3 prose-hr:border-[var(--border-color)]">
                    <ReactMarkdown>{aiInsight}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const InfoRow = ({ icon: Icon, value, isLink }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-5 h-5 text-muted" weight="duotone" />
    {isLink ? (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline text-sm truncate">{value}</a>
    ) : (
      <span className="text-secondary text-sm truncate">{value}</span>
    )}
  </div>
);

const StatRow = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${color === "gold" ? "bg-bch-gold/10" : "bg-bch-teal/10"}`}>
        <Icon className={`w-5 h-5 ${color === "gold" ? "text-bch-gold" : "text-bch-teal"}`} weight="duotone" />
      </div>
      <span className="text-secondary">{label}</span>
    </div>
    <span className={`font-mono font-bold ${color === "gold" ? "text-bch-gold" : "text-bch-teal"}`}>{value}</span>
  </div>
);
